import React, { useEffect, useState } from "react";
import api from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";

const EncuestaModal = ({ encuesta, onClose, onEncuestaEnviada }) => {
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [detalleEncuesta, setDetalleEncuesta] = useState(null);
    const [respuestas, setRespuestas] = useState({});
    const [erroresRespuesta, setErroresRespuesta] = useState("");
    const [sending, setSending] = useState(false);

    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: 'Información',
        message: '',
        variant: 'info'
    });

    const currentUser = { idUsuario: getSessionUserId() };

    useEffect(() => {
        if (encuesta) {
            cargarDetalle();
        }
    }, [encuesta]);

    const cargarDetalle = async () => {
        setLoadingDetalle(true);
        setDetalleEncuesta(null);
        setRespuestas({});
        setErroresRespuesta("");

        try {
            const resPreguntas = await api.get(ENDPOINTS.encuestaPreguntas);
            const preguntasEncuesta = resPreguntas.data
                .filter(p => (p.idEncuesta || p.IdEncuesta) === (encuesta.idEncuesta || encuesta.id))
                .map(p => ({
                    ...p,
                    idEncuestaPregunta: p.idEncuestaPregunta || p.IdEncuestaPregunta,
                    idEncuesta: p.idEncuesta || p.IdEncuesta,
                    idTipoPregunta: p.idTipoPregunta || p.IdTipoPregunta,
                    pregunta: p.pregunta || p.Pregunta || p.descripcionPregunta || p.DescripcionPregunta || p.texto || p.Texto
                }));

            const preguntasCompletas = await Promise.all(
                preguntasEncuesta.map(async (p) => {
                    let detalleExtra = {};

                    if ((p.idTipoPregunta || p.IdTipoPregunta) === 2) {
                        const resOM = await api.get(ENDPOINTS.encuestaPreguntaOpcionMultiple);
                        const configOM = resOM.data.find(c => (c.idEncuestaPregunta || c.IdEncuestaPregunta) === p.idEncuestaPregunta);

                        if (configOM) {
                            const resOpciones = await api.get(ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones);
                            const opciones = resOpciones.data
                                .filter(o => (o.idRespuestaOpcionMultiple || o.IdRespuestaOpcionMultiple) === (configOM.idRespuestaOpcionMultiple || configOM.IdRespuestaOpcionMultiple))
                                .map(o => ({
                                    ...o,
                                    idOpcion: o.idOpcion ?? o.IdOpcion ?? o.idRespuestaOpcionMultipleOpcion ?? o.IdRespuestaOpcionMultipleOpcion,
                                    texto: o.texto ?? o.Texto,
                                    orden: o.orden ?? o.Orden,
                                    idRespuestaOpcionMultiple: o.idRespuestaOpcionMultiple ?? o.IdRespuestaOpcionMultiple
                                }))
                                .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                            detalleExtra = {
                                tipo: "opcionMultiple",
                                opciones: opciones,
                                permiteMultiple: configOM.permiteMultiple
                            };
                        }
                    } else if ((p.idTipoPregunta || p.IdTipoPregunta) === 3) {
                        detalleExtra = {
                            tipo: "verdaderoFalso",
                            opciones: [
                                { idOpcion: "V", texto: "Verdadero" },
                                { idOpcion: "F", texto: "Falso" }
                            ]
                        };
                    } else {
                        detalleExtra = { tipo: "textoLibre" };
                    }

                    return { ...p, ...detalleExtra };
                })
            );

            setDetalleEncuesta({
                ...encuesta,
                preguntas: preguntasCompletas
            });

            const respInit = {};
            if (encuesta.respondidaUsuario) {
                try {
                    const resIntentos = await api.get(ENDPOINTS.encuestaIntentos);
                    const intentoUsuario = resIntentos.data.find(
                        i => (i.idEncuesta || i.IdEncuesta) === (encuesta.idEncuesta || encuesta.id) &&
                            (i.idUsuario || i.IdUsuario) === currentUser.idUsuario
                    );

                    if (intentoUsuario) {
                        const resRespuestas = await api.get(ENDPOINTS.encuestaRespuestas);
                        const respuestasIntento = resRespuestas.data.filter(
                            r => (r.idIntento || r.IdIntento) === (intentoUsuario.idEncuestaIntento || intentoUsuario.IdEncuestaIntento)
                        );

                        const [resDetOM, resDetVF, resDetText] = await Promise.all([
                            api.get(ENDPOINTS.respuestaEncuestaOpcionMultipleDet),
                            api.get(ENDPOINTS.respuestaEncuestaVerdaderoFalso),
                            api.get(ENDPOINTS.respuestaEncuestaTextoLibre)
                        ]);

                        for (const pregunta of preguntasCompletas) {
                            const respuestasPregunta = respuestasIntento.filter(
                                r => (r.idPregunta || r.IdPregunta || r.idEncuestaPregunta || r.IdEncuestaPregunta) === pregunta.idEncuestaPregunta
                            );

                            if (respuestasPregunta.length > 0) {
                                const idsRespuestas = respuestasPregunta.map(r => r.idEncuestaRespuesta || r.IdEncuestaRespuesta);

                                if (pregunta.tipo === "opcionMultiple") {
                                    const opcionesSeleccionadas = resDetOM.data.filter(
                                        d => idsRespuestas.includes(d.idEncuestaRespuesta || d.IdEncuestaRespuesta)
                                    );

                                    if (pregunta.permiteMultiple) {
                                        respInit[pregunta.idEncuestaPregunta] = opcionesSeleccionadas.map(o => o.idOpcion || o.IdOpcion);
                                    } else {
                                        respInit[pregunta.idEncuestaPregunta] = opcionesSeleccionadas[0]?.idOpcion || opcionesSeleccionadas[0]?.IdOpcion || "";
                                    }
                                } else if (pregunta.tipo === "verdaderoFalso") {
                                    const respuestaVF = resDetVF.data.find(
                                        d => idsRespuestas.includes(d.idEncuestaRespuesta || d.IdEncuestaRespuesta)
                                    );
                                    respInit[pregunta.idEncuestaPregunta] = respuestaVF?.respuesta ? "V" : "F";
                                } else if (pregunta.tipo === "textoLibre") {
                                    const respuestaTexto = resDetText.data.find(
                                        d => idsRespuestas.includes(d.idEncuestaRespuesta || d.IdEncuestaRespuesta)
                                    );
                                    respInit[pregunta.idEncuestaPregunta] = respuestaTexto?.textoRespuesta || respuestaTexto?.TextoRespuesta || "";
                                }
                            } else {
                                respInit[pregunta.idEncuestaPregunta] = pregunta.permiteMultiple ? [] : "";
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error cargando respuestas previas:", error);
                    preguntasCompletas.forEach(p => {
                        respInit[p.idEncuestaPregunta] = p.permiteMultiple ? [] : "";
                    });
                }
            } else {
                preguntasCompletas.forEach(p => {
                    respInit[p.idEncuestaPregunta] = p.permiteMultiple ? [] : "";
                });
            }

            setRespuestas(respInit);

        } catch (error) {
            console.error("Error cargando detalle encuesta:", error);
        } finally {
            setLoadingDetalle(false);
        }
    };

    const handleChangeRespuesta = (preguntaId, valor) => {
        setRespuestas((prev) => ({
            ...prev,
            [preguntaId]: valor,
        }));
    };

    const handleChangeCheckbox = (preguntaId, opcionId, checked) => {
        setRespuestas(prev => {
            const current = Array.isArray(prev[preguntaId]) ? prev[preguntaId] : [];
            if (checked) {
                return { ...prev, [preguntaId]: [...current, opcionId] };
            } else {
                return { ...prev, [preguntaId]: current.filter(id => id !== opcionId) };
            }
        });
    };

    const validarRespuestas = () => {
        if (!detalleEncuesta) return false;
        let isValid = true;
        for (const p of detalleEncuesta.preguntas) {
            const respuesta = respuestas[p.idEncuestaPregunta];
            if (p.tipo === "textoLibre") {
                if (!respuesta || !respuesta.toString().trim()) {
                    setErroresRespuesta("Por favor completa las respuestas de texto.");
                    isValid = false;
                    break;
                }
            } else if (p.tipo === "opcionMultiple" && p.permiteMultiple) {
                if (!respuesta || respuesta.length === 0) {
                    setErroresRespuesta("Por favor selecciona al menos una opción.");
                    isValid = false;
                    break;
                }
            } else {
                if (!respuesta) {
                    setErroresRespuesta("Por favor responde todas las preguntas.");
                    isValid = false;
                    break;
                }
            }
        }
        if (isValid) setErroresRespuesta("");
        return isValid;
    };

    const handleEnviarEncuesta = async (e) => {
        e.preventDefault();
        if (!detalleEncuesta) return;
        if (detalleEncuesta.respondidaUsuario) return;
        if (!validarRespuestas()) return;

        setSending(true);
        try {
            const now = new Date().toISOString();
            const intentoPayload = {
                idEncuestaIntento: 0,
                idEncuesta: detalleEncuesta.idEncuesta,
                idUsuario: currentUser.idUsuario,
                fechaInicio: now,
                fechaFin: now,
                activo: true
            };

            const resIntento = await api.post(ENDPOINTS.encuestaIntentos, intentoPayload);
            const idIntento = resIntento.data.idEncuestaIntento || resIntento.data;

            for (const pregunta of detalleEncuesta.preguntas) {
                const valorRespuesta = respuestas[pregunta.idEncuestaPregunta];
                const respuestaPayload = {
                    idEncuestaRespuesta: 0,
                    idIntento: idIntento,
                    idPregunta: pregunta.idEncuestaPregunta,
                    fechaRespuesta: new Date().toISOString()
                };

                const resRespuesta = await api.post(ENDPOINTS.encuestaRespuestas, respuestaPayload);
                const idRespuesta = resRespuesta.data.idEncuestaRespuesta || resRespuesta.data;

                if (pregunta.tipo === "textoLibre") {
                    await api.post(ENDPOINTS.respuestaEncuestaTextoLibre, {
                        idRespuestaTextoLibre: 0,
                        idEncuestaRespuesta: idRespuesta,
                        textoRespuesta: valorRespuesta
                    });
                } else if (pregunta.tipo === "verdaderoFalso") {
                    await api.post(ENDPOINTS.respuestaEncuestaVerdaderoFalso, {
                        idRespuestaVerdaderoFalso: 0,
                        idEncuestaRespuesta: idRespuesta,
                        respuesta: valorRespuesta === "V"
                    });
                } else if (pregunta.tipo === "opcionMultiple") {
                    const valores = Array.isArray(valorRespuesta) ? valorRespuesta : [valorRespuesta];
                    for (const v of valores) {
                        await api.post(ENDPOINTS.respuestaEncuestaOpcionMultipleDet, {
                            idRespuestaOpcionMultipleDet: 0,
                            idEncuestaRespuesta: idRespuesta,
                            idOpcion: parseInt(v)
                        });
                    }
                }
            }

            setAlertDialog({
                open: true,
                title: 'Éxito',
                message: "Encuesta enviada exitosamente",
                variant: 'success',
                onConfirm: () => {
                    if (onEncuestaEnviada) onEncuestaEnviada();
                    onClose();
                }
            });

        } catch (error) {
            console.error("Error enviando encuesta:", error);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Error al enviar la encuesta",
                variant: 'error'
            });
        } finally {
            setSending(false);
        }
    };

    if (!encuesta) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
        }} onClick={onClose}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }} onClick={e => e.stopPropagation()}>

                {/* Header Slim - Sin fondo verde */}
                <div style={{
                    padding: '1.25rem 2rem',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        {encuesta.respondidaUsuario && (
                            <div style={{ fontSize: '1.5rem' }}>✅</div>
                        )}
                        <h2 style={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#1e293b',
                            margin: 0
                        }}>
                            {encuesta.nombre || encuesta.titulo}
                        </h2>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#94a3b8',
                        padding: '0.5rem',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>×</button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
                    {loadingDetalle || !detalleEncuesta ? (
                        <p style={{ textAlign: 'center', color: '#94a3b8' }}>Cargando preguntas...</p>
                    ) : (
                        <form onSubmit={handleEnviarEncuesta}>
                            <p style={{ color: '#475569', marginBottom: '2rem', lineHeight: 1.6 }}>
                                {encuesta.descripcion}
                            </p>

                            {detalleEncuesta.preguntas && detalleEncuesta.preguntas.map((pregunta, idx) => (
                                <div key={pregunta.idEncuestaPregunta} style={{ marginBottom: '2rem' }}>
                                    <p style={{ fontWeight: 600, color: '#1e293b', marginBottom: '1rem' }}>
                                        {idx + 1}. {pregunta.pregunta}
                                    </p>

                                    {pregunta.tipo === "textoLibre" && (
                                        <textarea
                                            disabled={encuesta.respondidaUsuario}
                                            value={respuestas[pregunta.idEncuestaPregunta] || ""}
                                            onChange={(e) => handleChangeRespuesta(pregunta.idEncuestaPregunta, e.target.value)}
                                            rows={3}
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                                            placeholder="Escribe tu respuesta..."
                                        />
                                    )}

                                    {pregunta.tipo === "verdaderoFalso" && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {pregunta.opciones.map((op, idx) => {
                                                const isSelected = respuestas[pregunta.idEncuestaPregunta] === op.idOpcion;
                                                const letter = String.fromCharCode(65 + idx);

                                                return (
                                                    <div
                                                        key={op.idOpcion}
                                                        onClick={() => !encuesta.respondidaUsuario && handleChangeRespuesta(pregunta.idEncuestaPregunta, op.idOpcion)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '1rem',
                                                            cursor: encuesta.respondidaUsuario ? 'default' : 'pointer',
                                                            padding: '0.85rem 1.25rem',
                                                            borderRadius: '10px',
                                                            background: isSelected
                                                                ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                                : '#f8fafc',
                                                            border: `1px solid ${isSelected ? '#10b981' : '#e2e8f0'}`,
                                                            transition: 'all 0.2s ease',
                                                            position: 'relative',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background: isSelected ? '#10b981' : 'white',
                                                            border: `2px solid ${isSelected ? '#059669' : '#cbd5e1'}`,
                                                            color: isSelected ? 'white' : '#64748b',
                                                            fontWeight: '700',
                                                            fontSize: '0.85rem',
                                                            flexShrink: 0
                                                        }}>
                                                            {letter}
                                                        </div>
                                                        <span style={{
                                                            flex: 1,
                                                            fontSize: '0.95rem',
                                                            color: isSelected ? '#065f46' : '#334155',
                                                            fontWeight: isSelected ? '600' : '400'
                                                        }}>
                                                            {op.texto}
                                                        </span>
                                                        {isSelected && (
                                                            <div style={{
                                                                background: '#10b981',
                                                                color: 'white',
                                                                padding: '2px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: '0.7rem',
                                                                fontWeight: '700',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                Seleccionado
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {pregunta.tipo === "opcionMultiple" && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {pregunta.permiteMultiple ? (
                                                pregunta.opciones.map((op, idx) => {
                                                    const finalId = op.idRespuestaOpcionMultipleOpcion || op.idOpcion || op.id || idx;
                                                    const isChecked = (respuestas[pregunta.idEncuestaPregunta] || []).includes(finalId);
                                                    const letter = String.fromCharCode(65 + idx);

                                                    return (
                                                        <div
                                                            key={finalId}
                                                            onClick={() => !encuesta.respondidaUsuario && handleChangeCheckbox(pregunta.idEncuestaPregunta, finalId, !isChecked)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '1rem',
                                                                cursor: encuesta.respondidaUsuario ? 'default' : 'pointer',
                                                                padding: '0.85rem 1.25rem',
                                                                borderRadius: '10px',
                                                                background: isChecked
                                                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                                    : '#f8fafc',
                                                                border: `1px solid ${isChecked ? '#10b981' : '#e2e8f0'}`,
                                                                transition: 'all 0.2s ease',
                                                                position: 'relative',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: isChecked ? '#10b981' : 'white',
                                                                border: `2px solid ${isChecked ? '#059669' : '#cbd5e1'}`,
                                                                color: isChecked ? 'white' : '#64748b',
                                                                fontWeight: '700',
                                                                fontSize: '0.85rem',
                                                                flexShrink: 0
                                                            }}>
                                                                {letter}
                                                            </div>
                                                            <span style={{
                                                                flex: 1,
                                                                fontSize: '0.95rem',
                                                                color: isChecked ? '#065f46' : '#334155',
                                                                fontWeight: isChecked ? '600' : '400'
                                                            }}>
                                                                {op.texto}
                                                            </span>
                                                            {isChecked && (
                                                                <div style={{
                                                                    background: '#10b981',
                                                                    color: 'white',
                                                                    padding: '2px 10px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: '700',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    Seleccionado
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                pregunta.opciones.map((op, idx) => {
                                                    const finalId = op.idOpcion ?? op.IdOpcion ?? op.idRespuestaOpcionMultipleOpcion ?? op.IdRespuestaOpcionMultipleOpcion ?? idx;
                                                    const isChecked = String(respuestas[pregunta.idEncuestaPregunta]) === String(finalId);
                                                    const letter = String.fromCharCode(65 + idx);

                                                    return (
                                                        <div
                                                            key={finalId}
                                                            onClick={() => !encuesta.respondidaUsuario && handleChangeRespuesta(pregunta.idEncuestaPregunta, finalId)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '1rem',
                                                                cursor: encuesta.respondidaUsuario ? 'default' : 'pointer',
                                                                padding: '0.85rem 1.25rem',
                                                                borderRadius: '10px',
                                                                background: isChecked
                                                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                                    : '#f8fafc',
                                                                border: `1px solid ${isChecked ? '#10b981' : '#e2e8f0'}`,
                                                                transition: 'all 0.2s ease',
                                                                position: 'relative',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: isChecked ? '#10b981' : 'white',
                                                                border: `2px solid ${isChecked ? '#059669' : '#cbd5e1'}`,
                                                                color: isChecked ? 'white' : '#64748b',
                                                                fontWeight: '700',
                                                                fontSize: '0.85rem',
                                                                flexShrink: 0
                                                            }}>
                                                                {letter}
                                                            </div>
                                                            <span style={{
                                                                flex: 1,
                                                                fontSize: '0.95rem',
                                                                color: isChecked ? '#065f46' : '#334155',
                                                                fontWeight: isChecked ? '600' : '400'
                                                            }}>
                                                                {op.texto}
                                                            </span>
                                                            {isChecked && (
                                                                <div style={{
                                                                    background: '#10b981',
                                                                    color: 'white',
                                                                    padding: '2px 10px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: '700',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    Seleccionado
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {erroresRespuesta && (
                                <div style={{ backgroundColor: '#fee2e2', color: '#ef4444', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                                    {erroresRespuesta}
                                </div>
                            )}

                            {!encuesta.respondidaUsuario && (
                                <div style={{ textAlign: 'right', marginTop: '2rem' }}>
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        className="intra-button-primary"
                                        style={{
                                            cursor: sending ? 'wait' : 'pointer',
                                            opacity: sending ? 0.7 : 1
                                        }}
                                    >
                                        {sending ? 'Enviando...' : 'Enviar respuestas'}
                                    </button>
                                </div>
                            )}
                        </form>
                    )}
                </div>
            </div>

            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    setAlertDialog({ ...alertDialog, open: false });
                    if (alertDialog.onConfirm) alertDialog.onConfirm();
                }}
                buttonText="Aceptar"
            />
        </div>
    );
};

export default EncuestaModal;
